import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon as IconifyIcon } from '@iconify/react';
import { useI18n } from '../../lib/i18n';

const FaqSection5 = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const { t } = useI18n()

  const toggleIndex = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqItems = [
    { question: t.faqSection.q1, answer: t.faqSection.a1 },
    { question: t.faqSection.q2, answer: t.faqSection.a2 },
    { question: t.faqSection.q3, answer: t.faqSection.a3 },
  ];

  return (
    <section className="relative bg-white py-20 md:py-32 overflow-hidden">
      {/* Subtle Background Decoration for Left Column */}
      <div className="absolute left-0 top-0 w-1/3 h-full bg-gradient-to-r from-[#0052CC]/5 to-transparent pointer-events-none hidden lg:block" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          
          {/* Left Column - Sticky */}
          <div className="lg:col-span-4 lg:sticky lg:top-32 h-fit space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-3 py-1 rounded-full bg-[#0052CC]/10 text-[#0052CC] text-xs font-bold tracking-wider mb-4">
                {t.faqSection.badge}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#172B4D] tracking-tight mb-4">
                {t.faqSection.title}
              </h2>
              <p className="text-lg text-[#172B4D]/70 leading-relaxed">
                {t.faqSection.subtitle}
              </p>
            </motion.div>

            {/* Support Card */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#F4F5F7] rounded-xl p-6 border border-gray-100 shadow-sm relative overflow-hidden group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <img 
                    src="/images/support-avatar.png"
                     
                    alt="Support Agent" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-[#172B4D]">{t.faqSection.stillQuestions}</h3>
                  <p className="text-sm text-[#172B4D]/60">{t.faqSection.weHelpGladly}</p>
                </div>
              </div>

              <p className="text-[#172B4D]/80 text-sm mb-6">
                {t.faqSection.notFound}
              </p>

              <a href="/kontakt-valet-parken-frankfurt" className="w-full py-3 px-4 bg-white border border-[#0052CC]/20 text-[#0052CC] font-semibold rounded-lg hover:bg-[#0052CC] hover:text-white transition-colors duration-300 flex items-center justify-center gap-2 group/btn">
                <span>{t.faqSection.contactUs}</span>
                <IconifyIcon icon="ph:arrow-right-bold" className="group-hover/btn:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>

          {/* Right Column - FAQ List */}
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="space-y-2">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="border-b border-gray-100 last:border-none"
                >
                  <button
                    onClick={() => toggleIndex(index)}
                    className={`w-full text-left py-6 flex items-start justify-between group transition-colors duration-300 rounded-lg px-2 md:px-4 ${activeIndex === index ? 'bg-transparent' : 'hover:bg-gray-50'}`}
                  >
                    <span className={`text-lg font-medium transition-colors duration-300 pr-8 ${activeIndex === index ? 'text-[#0052CC]' : 'text-[#172B4D] group-hover:text-[#0052CC]'}`}>
                      {item.question}
                    </span>
                    <span className="flex-shrink-0 mt-1">
                      <motion.div
                        animate={{ rotate: activeIndex === index ? 45 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className={`p-1 rounded-full ${activeIndex === index ? 'bg-[#0052CC]/10 text-[#0052CC]' : 'text-[#172B4D]/40 group-hover:text-[#0052CC]'}`}
                      >
                        <IconifyIcon icon="ph:plus-bold" width={20} height={20} />
                      </motion.div>
                    </span>
                  </button>
                  
                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="pb-6 px-2 md:px-4 text-base leading-relaxed text-[#172B4D]/80">
                          {item.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FaqSection5;